import { Candidate } from "../Models";

export interface CSVColumn {
    key: string;
    label: string;
    getValue: (candidate: Candidate) => string | number;
}

export const availableColumns: CSVColumn[] = [
    {
        key: "firstName",
        label: "First Name",
        getValue: (c) => c.firstName,
    },
    {
        key: "lastName",
        label: "Last Name",
        getValue: (c) => c.lastName,
    },
    {
        key: "email",
        label: "Email",
        getValue: (c) => c.email,
    },
    {
        key: "age",
        label: "Age",
        getValue: (c) => c.age > 100 ? "-" : Math.floor(c.age),
    },
    {
        key: "agePercentile",
        label: "Age Percentile",
        getValue: (c) => c.agePercentile.toFixed(2),
    },
    {
        key: "workLocation1",
        label: "Location 1",
        getValue: (c) => c.workLocation1City?.name || "-",
    },
    {
        key: "workLocation2",
        label: "Location 2",
        getValue: (c) => c.workLocation2City?.name || "-",
    },
    {
        key: "shippingDepartment",
        label: "Department",
        getValue: (c) => [...Array.from(new Set(c.shippingDepartment || []))].join(", ") || "-",
    },
    {
        key: "experienceInMonths",
        label: "Experience (Months)",
        getValue: (c) => c.experienceInMonths,
    },
    {
        key: "experiencePercentileMonths",
        label: "Experience Percentile",
        getValue: (c) => c.experiencePercentileMonths.toFixed(2),
    },
    {
        key: "onBoardExperienceInMonths",
        label: "OnBoard Experience (Months)",
        getValue: (c) => c.onBoardExperienceInMonths || 0,
    },
    {
        key: "onBoardExperiencePercentileMonths",
        label: "OnBoard Experience Percentile",
        getValue: (c) => c.onBoardExperiencePercentileMonths?.toFixed(2) || "0",
    },
    {
        key: "totalEducationGrade",
        label: "Education Grade",
        getValue: (c) => c.totalEducationGrade.toFixed(2),
    },
    {
        key: "totalMaritimeAcademyEducationGrade",
        label: "Maritime Education Grade",
        getValue: (c) => c.totalMaritimeAcademyEducationGrade?.toFixed(2) || "0",
    },
    {
        key: "totalExperienceGrade",
        label: "Work Grade",
        getValue: (c) => c.totalExperienceGrade.toFixed(2),
    },
    {
        key: "totalOnBoardExperienceGrade",
        label: "OnBoard Work Grade",
        getValue: (c) => c.totalOnBoardExperienceGrade?.toFixed(2) || "0",
    },
    {
        key: "combinedGrade",
        label: "CV Grade",
        getValue: (c) => c.combinedGrade.toFixed(2),
    },
    {
        key: "combinedGradePercentile",
        label: "CV Grade Percentile",
        getValue: (c) => c.combinedGradePercentile.toFixed(2),
    },
    {
        key: "combinedPercentileBasedAge",
        label: "CV Grade (Age Based)",
        getValue: (c) => c.combinedPercentileBasedAge.toFixed(2),
    },
    {
        key: "combinePercentileBasedEXP",
        label: "CV Grade (Exp Based)",
        getValue: (c) => c.combinePercentileBasedEXP.toFixed(2),
    },
    {
        key: "totalGapMonths",
        label: "Gap (Months)",
        getValue: (c) => c.totalGapMonths,
    },
    {
        key: "gappercentile",
        label: "Gap Percentile",
        getValue: (c) => c.gappercentile.toFixed(2),
    },
    {
        key: "score",
        label: "Assessment Score",
        getValue: (c) => c.score !== null ? c.score.toFixed(2) : "-",
    },
    {
        key: "scorePercentile",
        label: "Score Percentile",
        getValue: (c) => c.scorePercentile?.toFixed(2) || "-",
    },
    {
        key: "noticePeriod",
        label: "Notice Period (Days)",
        getValue: (c) => c.noticePeriod || "-",
    },
    {
        key: "yearsOfExperience",
        label: "Years of Experience",
        getValue: (c) => c.yearsOfExperience || "-",
    },
    {
        key: "expectedGraduationDate",
        label: "Expected Graduation Date",
        getValue: (c) => c.expectedGraduationDate || "-",
    },
    {
        key: "latestStatusDate",
        label: "Latest Status Date",
        getValue: (c) => new Date(c.latestStatusDate).toLocaleDateString(),
    },
    {
        key: "cvMismatchError",
        label: "CV Errors",
        getValue: (c) => c.cvMismatchError || "-",
    },
    {
        key: "isEducationDetailsMismatch",
        label: "Education Details Mismatch",
        getValue: (c) => c.isEducationDetailsMismatch ? "Yes" : "No",
    },
    {
        key: "isEducationPeriodMismatch",
        label: "Education Period Mismatch",
        getValue: (c) => c.isEducationPeriodMismatch ? "Yes" : "No",
    },
    {
        key: "isExperienceDetailsMismatch",
        label: "Experience Details Mismatch",
        getValue: (c) => c.isExperienceDetailsMismatch ? "Yes" : "No",
    },
    {
        key: "isExperiencePeriodMismatch",
        label: "Experience Period Mismatch",
        getValue: (c) => c.isExperiencePeriodMismatch ? "Yes" : "No",
    },
];

export function exportToCSV(
    candidates: Candidate[],
    selectedColumns: string[],
    filename: string = "candidates.csv"
) {
    // Filter columns based on selection
    const columnsToExport = availableColumns.filter((col) =>
        selectedColumns.includes(col.key)
    );

    // Create CSV header
    const headers = columnsToExport.map((col) => col.label).join(",");

    // Create CSV rows
    const rows = candidates.map((candidate) => {
        return columnsToExport
            .map((col) => {
                const value = col.getValue(candidate);
                // Escape commas and quotes in values
                const stringValue = String(value);
                if (stringValue.includes(",") || stringValue.includes('"')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            })
            .join(",");
    });

    // Combine headers and rows
    const csv = [headers, ...rows].join("\n");

    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// Function to export with skill scores
export function exportToCSVWithSkills(
    candidates: Candidate[],
    selectedColumns: string[],
    includeSkills: boolean = false,
    filename: string = "candidates.csv"
) {
    // Get all unique skills
    const allSkills = includeSkills
        ? Array.from(
            new Set(
                candidates.flatMap((c) =>
                    c.avgSkillScores?.map((s) => s.skillName) || []
                )
            )
        )
        : [];

    // Filter base columns
    const columnsToExport = availableColumns.filter((col) =>
        selectedColumns.includes(col.key)
    );

    // Create headers
    const baseHeaders = columnsToExport.map((col) => col.label);
    const skillHeaders = allSkills.flatMap((skill) => [
        `${skill} Score`,
        `${skill} Percentile`,
    ]);
    const headers = [...baseHeaders, ...skillHeaders].join(",");

    // Create rows
    const rows = candidates.map((candidate) => {
        const baseValues = columnsToExport.map((col) => {
            const value = col.getValue(candidate);
            const stringValue = String(value);
            if (stringValue.includes(",") || stringValue.includes('"')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        });

        const skillValues = allSkills.flatMap((skillName) => {
            const skillScore = candidate.avgSkillScores?.find(
                (s) => s.skillName === skillName
            );
            return [
                skillScore?.skillScore?.toFixed(2) || "-",
                skillScore?.percentile?.toFixed(2) || "-",
            ];
        });

        return [...baseValues, ...skillValues].join(",");
    });

    // Combine and download
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
